import {BaseElement, MsgLabel, OMsgLabelInit, OSkinableInit} from "back/commons/basis";
import {IReg, IRegPointer, REG} from "lib/commons/registry";
import {DOMSH} from "lib/commons/xml/domsh";
import {IItemUiEnv, SrcMain} from "back/wsp/views/itemMain";
import {DOM, JSX, ShadowJsx} from "lib/commons/xml/dom";
import {renderAppend, TemplateResult, xhtml} from "lib/commons/utils/htmlLit";
import {JSrcFields, RESPS, SRC} from "lib/wsp/src";
import {ITooltipPointer, POPUP, PopupFloating, PopupTooltip} from "back/commons/widgets/popups";
import {WSP} from "lib/wsp/wsp";
import {EItResp, InfoRefreshItemDisplays, IShortDescCtx, ITEM} from "lib/wsp/item";
import {IView, OViewsContainerInit, ViewsContainer} from "lib/commons/views";
import {SrcGrid} from "back/wsp/widgets/srcGrid";
import {FocusItemSelRef} from "back/wsp/actions/srcActions";
import {ETaskStage, LIFECYCLE} from "lib/wsp/lcTask";
import {BarActions, OBarActionsInit} from "back/commons/widgets/bars";
import {Tasks} from "back/wsp/views/tasks";
import {AccelKeyMgr, ACTION} from "lib/commons/actions";
import {LANG, LOCALE} from "lib/commons/lang";
import {OUserRefInit} from "back/core/widgets/userRef";
import {Button, OButtonInit} from "back/commons/widgets/buttons";
import {IOpenForeignItemSvc} from "back/wsp/actions/wspActions";
import {ORespEditFieldInit, RespEditField} from "back/wsp/widgets/resps/respEdit";
import {Tab, Tabs} from "back/commons/widgets/tabs";
import {IInfo, IInfoConsumer} from "lib/commons/infos";
import {DynGen, ODynGenInit} from "back/wsp/views/dynGen";

export interface IRibbonAreaContext extends IRegPointer<IItemUiEnv> {
}

export class RibbonBase extends BaseElement implements ITooltipPointer, IInfoConsumer {

	reg: IReg<IItemUiEnv>;

	get shortDescCtx(): IShortDescCtx {return DOMSH.findHost<SrcMain>(this)}

	get ribbonMode(): 'static' | 'hover' | 'button' {return (this.getAttribute('role') as 'static' | 'hover' | 'button' | null) || 'static'}

	set ribbonMode(mode: 'static' | 'hover' | 'button') {
		if (this.ribbonMode === mode) return;
		DOM.setAttr(this, 'mode', mode);
		if (mode === 'static') {
			DOM.setAttr(this, 'role', null);
			this.tabIndex = -1;
			this.onkeypress = null;
			//if(this.matches(':focus')) this.blur(); utile ?
		} else {
			DOM.setAttr(this, 'role', mode);
			this.tabIndex = 0;
			this.onkeypress = this.onKeyPress;
		}
	}

	/** Associe un tooltip à ce ribbon. */
	protected setTooltip(factory: (owner: HTMLElement, tooltip: PopupTooltip) => IView | null, findFocusableOnShown?: boolean) {
		if (!factory) return;

		this.ribbonMode = 'hover';

		this.onclick = POPUP.promiseTooltip(this, findFocusableOnShown ?
				(owner: HTMLElement, tooltip: PopupTooltip): IView | null => {
					tooltip.addEventListener('c-shown', () => {
						const focusbale = DOMSH.findFlatNext(tooltip.firstElementChild, tooltip, DOM.IS_focusable);
						if (focusbale) focusbale.focus();
					});
					return factory(owner, tooltip);
				}
				: factory,
			{hoverAllowed: true, skin: "c-popup-floating", skinOver: "ribbon/tooltip"});
		this.onblur = (ev: FocusEvent) => {
			if (this.popupTooltip && ev.relatedTarget && !DOM.isAncestor(this.popupTooltip, ev.relatedTarget as HTMLElement)) this.popupTooltip.close();
		}
	}

	popupTooltip?: PopupTooltip;

	/** Associe une floating window à ce ribbon. */
	protected setFloating(factory: (ribbon: RibbonBase, popup: PopupFloating) => IView | null, findFocusableOnShown?: boolean) {
		if (!factory) return;
		this.ribbonMode = 'button';
		this.onclick = () => {
			const popup = POPUP.showFloating((popup: PopupFloating) => {
				if (findFocusableOnShown) {
					popup.addEventListener('c-shown', () => {
						const focusbale = DOMSH.findFlatNext(popup.firstElementChild, popup, DOM.IS_focusable);
						if (focusbale) focusbale.focus();
					});
				}
				this.popupFloating = popup;
				popup.addEventListener('c-close', () => {
					this.popupFloating = null;
				});
				return factory(this, popup);
			}, this, this, {closeOnBlur: true, skin: "c-popup-floating", skinOver: "ribbon/floating scroll/small"});
		}
	}

	popupFloating?: PopupFloating;


	/**
	 * Si pas de tooltip via setTooltip() ni de setFloating(), mais affichage d'un menu par ex, appelle this.click() après
	 * un temps de stabilisation de la souris au dessus du ribbon.
	 */
	protected callClickOnMouseover() {
		this.ribbonMode = 'button';
		POPUP.mouseOverRetarderCaller(this, false, () => this.click());
	}

	protected _initialize(init: any): void {
		this.reg = this.findReg(init);
		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("scroll/small", sr);
		this.reg.installSkin("wsp-rib", sr);
		this._initAndInstallSkin(this.localName, init);
		this.reg.env.infoBroker?.addConsumer(this);
	}

	/** Ribbon clickable. */
	onKeyPress(ev: KeyboardEvent) {
		if (LANG.in(ev.key, "Enter", " ") && !ACTION.isAnyControlPressed(ev)) {
			ev.stopImmediatePropagation();
			ev.preventDefault();
			this.click();
		}
	}

	protected _refresh() {
		super._refresh();
		if (this.popupTooltip) this.popupTooltip.close();
	}

	onViewHidden(closed?: boolean) {
		if (closed) this.reg?.env.infoBroker?.removeConsumer(this);
		if (this.popupTooltip) this.popupTooltip.close();
		if (this.popupFloating) this.popupFloating.close();
	}

	onInfo(info: IInfo): void {
		if (this.initialized) {
			if (info instanceof InfoRefreshItemDisplays) {
				const srcRef = info.srcRef;
				const currentSrcRef = SRC.srcRef(this.reg.env.longDesc);
				if (srcRef == currentSrcRef) this.refresh();
			}
		}
	}
}

REG.reg.registerSkin('wsp-rib', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		display: block;
		padding: 2px;
		max-height: 4em;
		text-align: center;
		font-size: var(--label-size);
		overflow: hidden;
		user-select: none;
	}

	:host([mode=button]) {
		cursor: pointer;
	}

	:host(:focus) {
		outline: var(--focus-outline);
		outline-offset: -1px;
	}

	.smallTi {
		color: var(--fade-color);
		font-style: italic;
		font-size: 85%;
	}
`);

REG.reg.registerSkin("ribbon/tooltip", 1, /* language=CSS */ `
	#content {
		display: block;
		padding: .2em;
		z-index: 101;
	}
`);

REG.reg.registerSkin("ribbon/floating", 1, /* language=CSS */ `
	#content {
		max-width: 40vw;
	}
`);


/**
 *
 */
export class RibbonItemInfo extends RibbonBase {

	template: (rib: RibbonItemInfo) => TemplateResult;
	templateTt: (rib: RibbonItemInfo) => TemplateResult;

	protected _initialize(init: any): void {
		super._initialize(init);
		this.setAttribute("c-resizable", "");

		this.template = this.reg.getSvc("ribbon.item.info.tpl") || RibbonItemInfo._template;
		this.templateTt = this.reg.getSvc("ribbon.item.info.tplTt") || RibbonItemInfo._templateTt;

		this.reg.env.longDescChange.add(this.onLongDescChange.bind(this));
		this.setTooltip(this._tooltip);
		this.setAttribute("draggable", "");
		this.ondragstart = (ev: DragEvent) => ITEM.setShortDescTransferToDragSession({
			shortDescs: [this.reg.env.longDesc],
			emitter: this,
			reg: this.reg
		}, ev, 'native');
		this.ondragend = ITEM.resetShortDescTransferToDragSession;
	}

	_tooltip(this: void, owner: RibbonItemInfo, tooltip: PopupTooltip) {
		const root = <div id="ttRoot"/> as HTMLElement;
		owner.reg.installSkin(owner.localName, root.attachShadow(DOMSH.SHADOWDOM_INIT));
		tooltip.addEventListener('c-show', (ev: CustomEvent) => {
			renderAppend(owner.templateTt(owner), root.shadowRoot);
		});
		return root;
	}

	static _template(this: void, rib: RibbonItemInfo) {
		const longDesc = rib.reg.env.longDesc;
		const type = rib.reg.env.wsp.wspMetaUi.getItemType(longDesc.itModel);
		if (ITEM.isExtItem(longDesc.srcUri)) {
			return xhtml`<div>
<div id="itTi">${longDesc.itTi}</div>
<img align="center" src="${type.getIcon(longDesc)}"/><span id="srcCd">${SRC.extractLeafFromUri(longDesc.srcUri)}</span>
<span id="foreignItem"> (item étranger)</span>
<span id="itModel">${type.getTitle(longDesc)}</span></div>`;
		} else {
			return xhtml`<div>
<div id="itTi">${longDesc.itTi}</div>
<img align="center" src="${type.getIcon(longDesc)}"/><span id="srcCd">${SRC.extractLeafFromUri(longDesc.srcUri)}</span>
<span id="itModel">${type.getTitle(longDesc)}</span></div>`;
		}
	}

	static _templateTt(this: void, rib: RibbonItemInfo) {
		const longDesc = rib.reg.env.longDesc;
		const dt = new Date(longDesc.srcDt);
		const foreignActions = !ITEM.isExtItem(longDesc.srcUri) ? undefined :
			xhtml`<div id="actions">
				<c-button class="inline" i.="${{reg: rib.reg, icon: "/@skin@/wsp/actions/openItem.svg", title: "Ouvrir cet item étranger dans son atelier"} as OButtonInit}" e.click="${function (this: Button) {
				rib.reg.getSvc<IOpenForeignItemSvc>("openForeignItem")(rib.reg, WSP.extractWspCdFromWspRef(longDesc.itFullUriInOwnerWsp), WSP.extractSrcRefFromWspRef(longDesc.itFullUriInOwnerWsp));
			}}"/>
			</div>`;
		return xhtml`${foreignActions}<div id="srcDt">modifié le <span class="val">${dt.toLocaleDateString("fr")} à ${dt.toLocaleTimeString("fr")}</span></div>
<div id="srcUser" b.hidden="${!longDesc.srcUser}">par <c-userref class="inline" i.="${{reg: rib.reg, nickOrAccount: longDesc.srcUser} as OUserRefInit}"/></div>
${rib.reg.getPref("wsp.hideWspStruct") ? undefined : xhtml`<div id="path">${ITEM.getItemUriLabel(longDesc.srcUri, longDesc)}</div>`}`;
	}

	protected _refresh() {
		super._refresh();
		renderAppend(this.template(this), this.shadowRoot);
	}

	onLongDescChange(ldNew: JSrcFields, ldOld: JSrcFields) {
		this.refresh();
	}
}

REG.reg.registerSkin('wsp-rib-info', 1, /* language=CSS */ `
	:host {
		flex: 1 1 auto;
		display: flex;
		flex-direction: column;
		justify-content: center;
		text-align: start;
		padding: 0 .5em;
	}

	#itModel {
		margin-inline-end: 1em;
		font-style: italic;
	}

	#itModel::before {
		content: "· ";
	}

	#itTi {
		color: var(--alt1-color);
		font-weight: bold;
	}

	img {
		cursor: grab;
		filter: var(--filter);
	}

	.val {
		color: var(--alt1-color);
		font-weight: bold;
	}

	#path {
		color: var(--fade-color);
	}

	#foreignItem {
		color: var(--alt1-color);
		font-weight: bold;
		font-style: italic;
	}

	:host(#ttRoot) {
		padding: .3em;
		font-size: var(--label-size);
	}
`);

customElements.define('wsp-rib-info', RibbonItemInfo);


/**
 *
 */
export class RibbonTabs extends RibbonBase {

	itemTabs: Tabs<SrcMain>

	protected _initialize(init: any) {
		super._initialize(init);
		this.reg.env.longDescChange.add(this.onLongDescChange.bind(this));
		this.itemTabs = DOMSH.findHost<SrcMain>(this)?.tabs;
		if (this.itemTabs) {
			const parent = this.itemTabs.shadowRoot.getElementById("tabs");
			const ctn = this.shadowRoot.appendChild(<div id="ctn"/>);
			//console.log("parentTabss:::",parent);
			ctn.style.minWidth = this.computeWidth(parent);
			new IntersectionObserver(cb => {
					//console.log("cb[0].intersectionRatio:::", cb[0].intersectionRatio);
					if (cb[0].intersectionRatio > .9) {
						//On peut insérer les onglets
						while (parent.firstElementChild) ctn.appendChild(parent.firstElementChild);
						DOM.setHidden(parent.parentElement, true);
					} else {
						//Place insuffisante, on replace dans l'emplacement d'origine
						while (ctn.firstElementChild) parent.appendChild(ctn.firstElementChild);
						DOM.setHidden(parent.parentElement, false);
					}
				},
				// [.8, 1] et margin V 10px pour éviter erreur d'arrondis si zoom du nav et instabilité ou bloquage.
				{root: this, threshold: [.8, 1], rootMargin: "10px 0px"}).observe(ctn);
		}
	}

	protected computeWidth(parent: HTMLElement) {
		let min: number, max: number;
		if (getComputedStyle(this).direction === "rtl") {
			min = (parent.lastElementChild as HTMLElement).offsetLeft;
			max = (parent.firstElementChild as HTMLElement).offsetLeft + parent.firstElementChild.clientWidth;
		} else {
			min = (parent.firstElementChild as HTMLElement).offsetLeft;
			max = (parent.lastElementChild as HTMLElement).offsetLeft + parent.lastElementChild.clientWidth;
		}
		return ((max - min) * 1.1) + "px";
	}

	protected onLongDescChange() {
		const ctn = this.shadowRoot.getElementById("ctn");
		if(!ctn) return;
		if (ctn.firstElementChild) {
			//Les onglets sont ici, dans le ribbon, on les refresh (visibilité perms)
			for (let tab = ctn.firstElementChild as Tab; tab; tab = tab.nextElementSibling as Tab) {
				tab.refresh();
			}
			ctn.style.minWidth = this.computeWidth(ctn);
		} else {
			//Les onglets sont à leur place initiale.
			ctn.style.minWidth = this.computeWidth(this.itemTabs.shadowRoot.getElementById("tabs"));
		}
	}
}

REG.reg.registerSkin('wsp-rib-tabs', 1, /* language=CSS */ `
	:host {
		flex: 100 100 0;
		display: flex;
		min-width: 0;
		min-height: 0;
		justify-content: center;
		align-items: flex-end;
		overflow: visible;
		padding: 0;
	}

	#ctn {
		display: flex;
		min-width: 0;
		min-height: 0;
		justify-content: center;
		border-top: 1px solid transparent;
	}

	c-tab {
		flex-direction: column;
		min-width: var(--tab-size, 6em);
		border-bottom: solid 2px transparent;
	}

	.selected {
		border-color: var(--tabl-select-color);
		color: var(--tabl-select-color);
		-webkit-text-stroke: .5px var(--tabl-select-color);
	}
`);

customElements.define('wsp-rib-tabs', RibbonTabs);


/**
 * Items référençant cet item.
 */

/** Configuration de liste de liens */
export interface JRibbonRefsConf {

	label: string

	description?: string

	entriesMsg?: (nb: number | undefined) => string

	emptyGridMsg?: string

	shortViewRefs?: (this: HTMLElement, ctx: IRegPointer<IItemUiEnv>, result: JSrcFields[]) => TemplateResult

	fetchSrcList: (this: HTMLElement, ctx: IRegPointer<IItemUiEnv>) => Promise<JSrcFields[]>

	isVisible?: (this: HTMLElement, ctx: IRegPointer<IItemUiEnv>) => boolean

}

export interface ORibbonRefsInit extends OSkinableInit {
	/** Code de la liste fournissant les refs à exploiter dans ce ribon. valeur par défaut : ribbon.item.refs.details
	 *		La **première** entrée visible de la liste est celle qui alimente le total (forme repliée))
	 */
	refsListCode: string
}

export class RibbonRefs extends RibbonBase {

	confs: JRibbonRefsConf[];

	private conf: ORibbonRefsInit;

	private _noFetch = true;

	private _currentDetailsConf: JRibbonRefsConf;

	static DEFAULT_REFS_LIST = 'ribbon.item.refs.confs';

	private _total: number;

	protected _initialize(init: ORibbonRefsInit): void {
		this.conf = Object.assign({
			refsListCode: RibbonRefs.DEFAULT_REFS_LIST,
		}, init);
		super._initialize(this.conf);

		if (ITEM.isHistoryOrTrashUri(this.reg.env.longDesc.srcUri)) {
			this.hidden = true;
			return;
		}

		this.confs = this.reg.mergeLists(this.conf.refsListCode);
		this.confs = this.confs?.filter(entry => !entry.isVisible || entry.isVisible.call(this, this));
		this.setFloating(this.reg.getSvc("ribbon.item.refs.details") || this._details);

		// Load retardé pour donner la priorité au contenu
		setTimeout(async () => {
			this._noFetch = false;
			this.refresh();
		}, 500);
	}

	async fetch(this: void, rib: RibbonRefs): Promise<JSrcFields[]> {
		return await rib.confs[0].fetchSrcList.call(rib, rib);
	}

	private redrawShortView(result?: JSrcFields[]): void {
		const total = result ? result.length : undefined;
		const defaultConf = this.confs[0];
		const tpl = defaultConf.shortViewRefs
			? defaultConf.shortViewRefs.call(this, this, result)
			: xhtml`<div id="total" title="${(defaultConf.entriesMsg ? defaultConf.entriesMsg(total) : null) || defaultConf.description || defaultConf.label}">${total !== undefined ? total : '...'}</div><div id="label">réf.</div>`;
		renderAppend(tpl, this.shadowRoot);
	}

	_details(this: void, rib: RibbonRefs, popup: PopupFloating): IView | null {

		const grid = new SrcGrid().initialize({
			reg: rib.reg,
			hideHeaders: true,
			autoSelOnFocus: "first",
			itemHandlingReact: rib.reg.env.infoBroker,
			defaultAction: FocusItemSelRef.SINGLETON,
			actions: rib.reg.mergeLists("actions:ribbonRef:shortDesc", "actions:wsp:shortDesc"),
			accelKeyMgr: new AccelKeyMgr().initFromMapActions(rib.reg.mergeListsAsMap("accelkeys:ribbonRef:shortDesc", "accelkeys:wsp:shortDesc")),
			emptyBody: () => <span>{rib._currentDetailsConf?.emptyGridMsg || 'Aucun item'}</span>
		});

		let confTitle = <div class="title"/> as HTMLDivElement;

		popup.addEventListener('c-show', async (ev: CustomEvent) => {
			//grid.dataHolder.setDatas([]);
			const wsp = rib.reg.env.wsp;
			confTitle.innerText = "";
			//grid.srcGridDatas.setDatas([]); flash UI

			if (rib.confs.length === 1) {
				rib._currentDetailsConf = rib.confs[0];
				confTitle.appendChild(<div>{rib._currentDetailsConf.label}</div>);
			} else {
				const select = confTitle.appendChild(<select onchange={async () => {
					//grid.srcGridDatas.setDatas([]); flash UI
					const pos = Number.parseInt(select.value);
					rib._currentDetailsConf = rib.confs[pos];
					const items = await rib._currentDetailsConf.fetchSrcList.call(rib, rib);
					grid.srcGridDatas.setDatas(items);
					grid.focus();
					if (pos === 0) rib.redrawShortView(items);
				}}/>) as HTMLSelectElement;
				rib.confs.forEach((entry, pos) => {
					select.appendChild(<option value={pos} label={entry.label} title={entry.description}/>);
				});
			}
			rib._currentDetailsConf = rib.confs[0];
			const items = await rib._currentDetailsConf.fetchSrcList.call(rib, rib);
			grid.srcGridDatas.setDatas(items);
			rib.redrawShortView(items);
			grid.focus();
		});

		return <div>
			<ShadowJsx skin="wsp-rib-refs" reg={rib.reg}>
				{confTitle}
				{grid}
			</ShadowJsx>
		</div>;
	}

	protected _refresh() {
		super._refresh();
		if (!this.hidden) {
			if (!this._noFetch)
				this.fetch(this).then((resp) => this.redrawShortView(resp));
			else
				this.redrawShortView();
		}

	}
}

/*
Trop proche de 'ribbonRef.conf.revlinks.item'. La différence est subtile => on laisse 'ribbonRef.conf.revlinks.item', le plus complet
REG.reg.registerSvc('ribbonRef.conf.items', 1, {
	label: "Items référençants",
	fetchSrcList: (ctx)=>WSP.fetchSearchJson(
		ctx.reg.env.wsp,
		this,
		new SearchRequest().addFieldColumns(...ctx.reg.env.wsp.getShortDescFields()).setWhere(new SearchLinkParents(SRC.srcRef(ctx.reg.env.longDesc))).toXml()
	),
} as JRibbonRefsConf);
REG.reg.addSvcToList(RibbonRefs.DEFAULT_REFS_LIST, 'items', 1, 'ribbonRef.conf.items');
 */

REG.reg.registerSvc('ribbonRef.conf.revlinks.item', 1, {
	label: "Références à cet item",
	fetchSrcList: async (ctx) => {
		const ascs = await ITEM.fetchSrcNetJson(ctx.reg.env.wsp, this, SRC.srcRef(ctx.reg.env.longDesc), ctx.reg.env.wsp.getShortDescDef(),
			"asc", true, null, null, 100, 1, null, 'itemOnly');
		return ascs?.lnks?.map(entry => entry.src);
	},
	entriesMsg: (nb) => nb != undefined ? `${nb} référence(s) à cet item` : null,
	emptyGridMsg: "Aucune",
} as JRibbonRefsConf);
REG.reg.addSvcToList(RibbonRefs.DEFAULT_REFS_LIST, 'revlinks.item', 1, 'ribbonRef.conf.revlinks.item', -1);

REG.reg.registerSvc('ribbonRef.conf.revlinks.all', 1, {
	label: "Références à cet item ou à ses ancres",
	fetchSrcList: async (ctx) => {
		const ascs = await ITEM.fetchSrcNetJson(ctx.reg.env.wsp, this, SRC.srcRef(ctx.reg.env.longDesc), ctx.reg.env.wsp.getShortDescDef(),
			"asc", true, null, null, 100, 1, null);
		return ascs?.lnks?.map(entry => entry.src);
	},
	entriesMsg: (nb) => nb != undefined ? `${nb} référence(s) à cet item ou à ses ancres` : null,
	emptyGridMsg: "Aucune",
} as JRibbonRefsConf);
// Ajouté SSi subItem dans le reg par modeling
//REG.reg.addSvcToList(RibbonRefs.DEFAULT_REFS_LIST, 'revlinks.all', 1, 'ribbonRef.conf.revlinks.all');

REG.reg.registerSkin('wsp-rib-refs', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		min-width: 2rem;
		--row-bgcolor: var(--bgcolor);
	}

	#total {
		color: var(--alt1-color);
		font-weight: bold;
	}

	#label {
		white-space: nowrap;
		font-style: italic;
		color: var(--fade-color);
	}

	.title {
		display: flex;
		min-height: 0;
		font-size: var(--label-size);
		padding: .2em;
		color: var(--fade-color);
		border-bottom: 1px solid var(--border-color);
		text-align: center;
		min-width: 12em;
		justify-content: center;
	}

	select {
		flex: 1;
		min-width: 0;
		background-color: var(--form-bgcolor);
		color: var(--form-color);
		border: 1px solid var(--border-color);
		text-overflow: ellipsis;
		font-size: inherit;
	}

	wsp-src-grid {
		border: none;
	}

`);


REG.reg.addToList("actions:ribbonRef:shortDesc", "focusItem", 1, FocusItemSelRef.SINGLETON);
customElements.define('wsp-rib-refs', RibbonRefs);


/**
 * Life-cyle de l'item.
 */
export class RibbonLc extends RibbonBase {

	template: (rib: RibbonLc) => TemplateResult;

	protected _initialize(init: any): void {
		super._initialize(init);
		this.reg.env.longDescChange.add(this.refresh.bind(this));
		this.template = this.reg.getSvc("ribbon.item.lc.tpl") || RibbonLc._template;
		this.setTooltip(RibbonLc._tooltip, true);
		//this.callClickOnMouseover();
		//this.onclick = (ev: MouseEvent) => {
		// 	POPUP.showPopupActions({
		// 		reg: this.reg,
		// 		actions: LIFECYCLE.listAllTransitionActions(this.reg.env.wsp.wspMetaUi),
		// 		actionContext: this.shortDescCtx,
		//		groupOrder: this.reg.env.wsp.wspMetaUi.getLcTransitionsGroupOrder(),
		// 		headBuilder: (root: HTMLElement) => {
		// 			root.append("Changé par ", <c-user>xx</c-user>, " le ", SRCDRAWER.dateFormatter.format(this.reg.env.longDesc.lcDt),
		// 				<div>Changer d'état :</div>)
		// 		}
		// 	}, this, this);
		// }
	}

	static _template(this: void, rib: RibbonLc): TemplateResult {
		const longDesc = rib.reg.env.longDesc;
		const lcState = rib.reg.env.wsp.wspMetaUi.getLcStateOrUnknown(longDesc.lcSt);
		return xhtml`<div class="smallTi">${rib.reg.env.wsp.wspMetaUi.getLcName()}</div><div id="lcSt"><img src="${lcState.iconUrl || ""}"/>${lcState.name}</div>`;
	}

	static _tooltip(this: void, rib: RibbonLc, tooltip: PopupTooltip): IView | null {
		const tpl = <div id="ttRoot">
			<ShadowJsx skin={rib.localName} reg={rib.reg}>
				<div id="last">
					<div>Changé le <span id="lastDt"/></div>
					<div>par <c-user id="lastUser"/></div>
				</div>
				<BarActions class="vertical" î={{
					reg: rib.reg,
					actions: LIFECYCLE.listAllTransitionActions(rib.reg.env.wsp.wspMetaUi),
					actionContext: rib.shortDescCtx,
					uiContext: "dialog",
					groupOrder: rib.reg.env.wsp.wspMetaUi.getLcTransitionsGroupOrder(),
					disableFullOverlay: true
				} as OBarActionsInit<IShortDescCtx>}/>
			</ShadowJsx>
		</div>;
		tooltip.addEventListener('c-show', () => {
			const ld = rib.reg.env.longDesc;
			if (ld.lcDt > 0) {
				DOM.setHidden(tpl.shadowRoot.getElementById("last"), false);
				DOM.setTextContent(tpl.shadowRoot.getElementById("lastDt"), LOCALE.formatDateDigitsToSec.format(ld.lcDt));
				DOM.setTextContent(tpl.shadowRoot.getElementById("lastUser"), ld.lcBy);
			} else {
				DOM.setHidden(tpl.shadowRoot.getElementById("last"), true);
			}
		});
		return tpl;
	}

	protected _refresh() {
		super._refresh();
		const longDesc = this.reg.env.longDesc;
		if (ITEM.isHistoryUri(longDesc.srcUri) || !LIFECYCLE.hasLcOnItem(longDesc, this.reg.env.wsp.wspMetaUi)) {
			DOM.setHidden(this, true);
		} else {
			DOM.setHidden(this, false);
			renderAppend(this.template(this), this.shadowRoot);
		}
	}
}

REG.reg.registerSkin('wsp-rib-lc', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		min-width: 5em;
		font-size: var(--label-size);
	}

	#lcSt {
		color: var(--alt2-color);
		font-weight: bold;
		padding: 0 .3em;
	}

	#lcSt > img {
		width: 1em;
		height: 1em;
		margin-inline-end: 0.2em;
	}

	:host(#ttRoot) {
		padding: .3em;
	}

	#last {
		padding: .3em 0;
		border-bottom: 1px solid var(--border-color);
	}

	#lastUser,
	#lastDt {
		color: var(--alt1-color);
		font-weight: bold;
	}
`);

customElements.define('wsp-rib-lc', RibbonLc);


/**
 * dynGen de l'item.
 */

export interface ORibbonDynGenInit extends OSkinableInit {
	dynGen: ODynGenInit,
	label?: string;
}

export class RibbonDynGen extends RibbonBase {

	template: (rib: RibbonDynGen) => TemplateResult;

	protected conf: ORibbonDynGenInit

	protected _initialize(init: ORibbonDynGenInit): void {
		super._initialize(init);
		this.conf = init;
		if (!this.conf.dynGen) this.conf.dynGen = {} as any;
		if (!("reg" in this.conf.dynGen)) this.conf.dynGen.reg = this.reg;
		if (!("autoResize" in this.conf.dynGen)) this.conf.dynGen.autoResize = true;
		if (!("w" in this.conf.dynGen)) this.conf.dynGen.w = this.reg.env.wsp.code;
		if (!("r" in this.conf.dynGen)) this.conf.dynGen.r = SRC.srcRef(this.reg.env.longDesc);

		this.reg.env.longDescChange.add(this.refresh.bind(this));
		this.template = this.reg.getSvc("ribbon.item.dynGen.tpl") || RibbonDynGen._template;
	}

	static _template(this: void, rib: RibbonDynGen): TemplateResult {
		const longDesc = rib.reg.env.longDesc;
		const dynGen = new DynGen().initialize(rib.conf.dynGen);
		dynGen.onViewShown();
		return xhtml`${dynGen}<div id="label">${rib.conf.label}</div>`;
	}

	protected _refresh() {
		super._refresh();
		const longDesc = this.reg.env.longDesc;
		if (ITEM.isHistoryUri(longDesc.srcUri)) {
			DOM.setHidden(this, true);
		} else {
			DOM.setHidden(this, false);
			renderAppend(this.template(this), this.shadowRoot);
		}
	}
}

REG.reg.registerSkin('wsp-rib-dyngen', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		min-width: 5em;
		font-size: var(--label-size);
		display: flex;
		flex-direction: column;
	}

	wsp-dyngen {
		overflow: auto;
	}

	#label {
		white-space: nowrap;
		font-style: italic;
		color: var(--fade-color);
	}

`);

customElements.define('wsp-rib-dyngen', RibbonDynGen);

/**
 * Ribbon d'actions
 */

export interface ORibbonBarActionsInit extends OSkinableInit {
	barActions: OBarActionsInit<IShortDescCtx>,
	label?: string;
}

export class RibbonBarActions extends RibbonBase {

	template: (rib: RibbonBarActions) => TemplateResult;

	protected conf: ORibbonBarActionsInit

	protected _initialize(init: ORibbonBarActionsInit): void {
		super._initialize(init);
		this.conf = init;
		this.reg.env.longDescChange.add(this.refresh.bind(this));
		this.template = this.reg.getSvc("ribbon.item.actions.tpl") || RibbonBarActions._template;
	}

	static _template(this: void, rib: RibbonBarActions): TemplateResult {
		const longDesc = rib.reg.env.longDesc;
		const barActions = new BarActions().initialize(
			Object.assign({
					uiContext: "custom",
					disableFullOverlay: true,
					overrideButtonsElts: (btn) => DOM.addClass(btn, "vertical")
				} as OBarActionsInit<IShortDescCtx>,
				rib.conf.barActions, {
					actionContext: rib.shortDescCtx
				} as OBarActionsInit<IShortDescCtx>));
		return xhtml`${barActions}<div id="label">${rib.conf.label}</div>`;
	}

	protected _refresh() {
		super._refresh();
		const longDesc = this.reg.env.longDesc;
		if (ITEM.isHistoryUri(longDesc.srcUri)) {
			DOM.setHidden(this, true);
		} else {
			DOM.setHidden(this, false);
			renderAppend(this.template(this), this.shadowRoot);
		}
	}
}

REG.reg.registerSkin('wsp-rib-baractions', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		font-size: var(--label-size);
		display: flex;
		flex-direction: column;
	}

	#label {
		white-space: nowrap;
		font-style: italic;
		color: var(--fade-color);
	}

`);

customElements.define('wsp-rib-baractions', RibbonBarActions);

/**
 * Responsabilities de l'item.
 */
export class RibbonResp extends RibbonBase {

	template: (rib: RibbonResp) => TemplateResult;

	protected _initialize(init: any): void {
		super._initialize(init);
		this.reg.env.longDescChange.add(this.refresh.bind(this));
		this.template = this.reg.getSvc("ribbon.item.resp.tpl") || RibbonResp._template;
		this.setFloating(this.reg.getSvc("ribbon.item.resp.details") || RibbonResp._details, true);
	}

	static _template(this: void, rib: RibbonResp): TemplateResult {
		const longDesc = rib.reg.env.longDesc;
		const rspUsrs = RESPS.toRespsList(longDesc.rspUsrs);
		const itemType = rib.reg.env.wsp.wspMetaUi.getItemType(longDesc.itModel);
		let resps = <div class="list"/>;

		let count = 0;
		let hasMore = false;
		for (const resp of itemType.datas.resps) {//.sort((e1, e2) => e2.required === e1.required ? 0 : (e2.required ? 1 : -1))
			let nbUsers = rspUsrs[resp.code]?.length;
			if (rspUsrs[resp.code] && nbUsers > 0) {
				/*if (count >= 2) {
					hasMore = true;
					break;
				}*/
				const keyElt = resps.appendChild(<div class="key">{resp.name}</div>);
				if (resp.desc) keyElt.title = resp.desc;
				const values = resps.appendChild(<div class="values"/>);
				values.appendChild(<c-userref class="inline" î={{reg: rib.reg, nickOrAccount: rspUsrs[resp.code][0]} as OUserRefInit}/>);
				if (--nbUsers > 0)
					values.appendChild(<span class="moreUsers">+{nbUsers}</span>)
				/*rspUsrs[resp.code].forEach((user) => {
					values.appendChild(<c-userref class="inline" î={{reg: rib.reg, nickOrAccount: user} as OUserRefInit}/>);
				});*/
				count++;
			}
		}

		if (count == 0 && itemType.hasResps())
			resps.appendChild(<MsgLabel id="noRespLabel" î={{label: "Responsabilités non définies", level: rib.isInError() ? "error" : "info"} as OMsgLabelInit}/>);

		return xhtml`${resps}`;//${hasMore ? <MsgLabel class="more" î={{label: "..."} as OMsgLabelInit}/> : ""}
	}

	static _details(this: void, rib: RibbonResp, popup: PopupFloating): IView | null {
		const longDesc = rib.reg.env.longDesc;
		const rspUsrs = RESPS.toRespsList(longDesc.rspUsrs);
		const itemType = rib.reg.env.wsp.wspMetaUi.getItemType(longDesc.itModel);
		let resps = <div class="list"/>;
		itemType.datas.resps.forEach((resp) => {
			// FIXME : afficher ici les resps inconnues de l'item ? A réévaluer...
			const keyElt = resps.appendChild(<div class="key">{resp.name}</div>);
			if (resp.desc) keyElt.title = resp.desc;
			const inptELt = resps.appendChild(<RespEditField class="values" î={{
				reg: rib.reg,
				resp: resp.code,
			} as ORespEditFieldInit}/>);
		});
		return <div>
			<ShadowJsx skin="wsp-rib-resp/details" reg={rib.reg}>
				<div class="title">Responsabilités</div>
				{resps}
			</ShadowJsx>
		</div>;
	}

	protected isInError(): boolean {
		const longDesc = this.reg.env.longDesc;
		return longDesc.rspSt === EItResp.errors
	}

	protected _refresh() {
		super._refresh();
		const longDesc = this.reg.env.longDesc;
		const itemType = this.reg.env.wsp.wspMetaUi.getItemType(longDesc.itModel);
		if (ITEM.isHistoryOrTrashUri(longDesc.srcUri) || !itemType.hasResps()) {
			DOM.setHidden(this, true);
		} else {
			DOM.setHidden(this, false);
			DOM.setAttrBool(this, "data-error", this.isInError());
			renderAppend(this.template(this), this.shadowRoot);
		}
	}
}

REG.reg.registerSkin('wsp-rib-resp', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		min-width: 5em;
		font-size: var(--label-size);
		display: flex;
	}

	:host(#ttRoot) {
		padding: .3em;
	}

	:host([data-error]) {
		box-shadow: inset 0 0 5px var(--error-color);
	}

	.list {
		overflow: auto;
		padding: 2px;
		display: grid;
		grid-template-columns: auto 1.5fr;
		grid-column-gap: .5em;
		grid-row-gap: .2em;
		align-items: center;
	}

	.key {
		text-align: start;
		align-self: start;
	}

	.values {
		text-align: end;
		align-self: start;
	}

	.moreUsers {
		color: var(--alt1-color);
		font-style: italic;
	}

	.moreUsers::before {
		content: ", ";
	}

	.values > c-userref + c-userref {
		margin-inline-start: 1em;
	}

	#noRespLabel {
		grid-column-start: 0;
		grid-column-end: 1;
	}

`);

REG.reg.registerSkin('wsp-rib-resp/details', 1, /* language=CSS */ `
	:host {
		/*min-width: 20em;*/
		width: 30em;
		max-width: 100%;
	}

	.title {
		font-size: var(--label-size);
		padding: .2em;
		color: var(--fade-color);
		border-bottom: 1px solid var(--border-color);
		text-align: center;
	}

	.list {
		padding: .2em;
		display: grid;
		grid-template-columns: auto 1fr;
		grid-column-gap: .5em;
		grid-row-gap: .5em;
		align-items: center;
	}

	.key {
		text-align: start;
	}

	.values {
		text-align: end;
		min-width: 10em;
	}
`);
customElements.define('wsp-rib-resp', RibbonResp);


/**
 * Tâches associées à l'item.
 */
export class RibbonTasks extends RibbonBase {

	template: (rib: RibbonTasks) => TemplateResult;

	fetch: (rib: RibbonTasks) => Promise<void>;

	pending: number;
	forthcoming: number;

	private _noFetch = true;

	protected _initialize(init: any): void {
		super._initialize(init);
		if (!this.reg.env.wsp.wspMetaUi.hasTasks) {
			this.hidden = true;
			return;
		}
		this.fetch = this.reg.getSvc("ribbon.item.tasks.fetch") || this._fetch;
		this.template = this.reg.getSvc("ribbon.item.tasks.tpl") || RibbonTasks._template;
		this.setFloating(this.reg.getSvc("ribbon.item.tasks.details") || RibbonTasks._details);

		//on retarde l'affichage des infos du ribbon pour laisser la priorité au contenu de l'item.
		setTimeout(async () => {
			this._noFetch = false;
			this.refresh();
		}, 300);
	}

	async _fetch(this: void, rib: RibbonTasks) {
		const counts = await WSP.fetchSrc(rib.reg.env.wsp, rib, SRC.srcRef(rib.reg.env.longDesc), ["tkPendingCount", "tkForthcomingCount"]);
		rib.pending = counts.tkPendingCount || 0;
		rib.forthcoming = counts.tkForthcomingCount || 0;
	}

	static _template(this: void, rib: RibbonTasks): TemplateResult {
		const pending = rib.pending;
		const forthcoming = rib.forthcoming;
		return xhtml`<div><span id="pending" title="Tâches en cours">${pending !== undefined ? pending : '...'}</span>
<span id="forthcoming" title="Tâches à venir">${forthcoming !== undefined ? `(${forthcoming})` : '...'}</span></div>
<div id="label">tâches</div>`;
	}

	static _details(this: void, rib: RibbonTasks, popup: PopupFloating): IView | null {
		const tasks = new Tasks().initialize({
			reg: rib.reg,
			ephemeralView: true,
			refreshCb: (visibleStage: ETaskStage, tasks: JSrcFields[]) => {
				if (visibleStage === ETaskStage.pending) DOM.setTextContent(rib.shadowRoot.getElementById('pending'), tasks.length.toString());
				else if (visibleStage === ETaskStage.forthcoming) DOM.setTextContent(rib.shadowRoot.getElementById('forthcoming'), `(${tasks.length.toString()})`);
			}
		});

		return <ViewsContainer î={{views: [tasks]} as OViewsContainerInit}>
			<ShadowJsx skin="wsp-rib-tasks/details" reg={rib.reg}>
				<div class="title">Tâches liées à cet item</div>
				{tasks}
			</ShadowJsx>
		</ViewsContainer>;
	}

	protected _refresh() {
		super._refresh();
		if (!this.hidden) {
			if (!this._noFetch)
				this.fetch(this).then(() => renderAppend(this.template(this), this.shadowRoot));
			else
				renderAppend(this.template(this), this.shadowRoot)
		}
	}
}

REG.reg.registerSkin('wsp-rib-tasks', 1, /* language=CSS */ `
	:host {
		flex: 0 1 auto;
		min-width: 3em;
		--row-bgcolor: var(--bgcolor);
	}

	#pending {
		color: var(--alt1-color);
		font-weight: bold;
	}

	#forthcoming {
		color: var(--alt2-color);
		font-weight: bold;
		font-size: .8em;
	}

	#label {
		white-space: nowrap;
		font-style: italic;
		color: var(--fade-color);
	}
`);

REG.reg.registerSkin('wsp-rib-tasks/details', 1, /* language=CSS */ `
	:host {
		min-width: 40em;
	}

	.title {
		font-size: var(--label-size);
		padding: .2em;
		color: var(--fade-color);
		border-bottom: 1px solid var(--border-color);
		text-align: center;
	}
`);

customElements.define('wsp-rib-tasks', RibbonTasks);


REG.reg.addToList("ribbon:item", "info", 1, RibbonItemInfo, 10);
REG.reg.addToList("ribbon:item", "tabs", 1, RibbonTabs, 15);
REG.reg.addToList("ribbon:item", "refs", 1, RibbonRefs, 500);

